import React, { useEffect, useRef } from 'react'

/**
 * Mounts a full standalone HTML document inside a React route while
 * preserving the original design and behaviour exactly:
 *
 *  - All <style> / <link rel="stylesheet"> blocks from the source <head>
 *    are appended to document.head (tagged so we can clean them up).
 *  - The <body> innerHTML is injected as-is into the route container.
 *  - Every <script> (inline or external) inside the original document
 *    is re-emitted in document order so they execute as a normal page
 *    load would. External scripts wait for `load` before the next one
 *    runs, mirroring browser parser semantics for sync scripts.
 *
 *  Body classes / attributes are mirrored onto <body> while the route
 *  is mounted, and torn down on unmount so route changes do not leak
 *  styles or globals from the previous page.
 */
export default function HtmlPage({ html, pageKey }) {
  const containerRef = useRef(null)

  useEffect(() => {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    rewriteRelativeUrls(doc)
    const marker = `pb-injected-${pageKey}`

    // --- 1. Title -------------------------------------------------------
    const prevTitle = document.title
    const titleEl = doc.querySelector('title')
    if (titleEl) document.title = titleEl.textContent || prevTitle

    // --- 2. <html> attrs (data-theme etc.) ------------------------------
    const htmlAttrs = []
    for (const attr of Array.from(doc.documentElement.attributes)) {
      if (attr.name === 'lang' || attr.name.startsWith('data-')) {
        htmlAttrs.push({ name: attr.name, prev: document.documentElement.getAttribute(attr.name) })
        document.documentElement.setAttribute(attr.name, attr.value)
      }
    }

    // --- 3. <body> attrs (classes, data-, style) ------------------------
    const bodyAttrs = []
    for (const attr of Array.from(doc.body.attributes)) {
      bodyAttrs.push({ name: attr.name, prev: document.body.getAttribute(attr.name) })
      document.body.setAttribute(attr.name, attr.value)
    }

    // --- 4. Head styles + stylesheet links ------------------------------
    const headInjections = []
    doc.head.querySelectorAll('style, link[rel="stylesheet"]').forEach((node) => {
      const clone = node.cloneNode(true)
      clone.setAttribute(marker, '')
      document.head.appendChild(clone)
      headInjections.push(clone)
    })

    // --- 5. Body innerHTML (without inline scripts; we re-run them) -----
    const bodyClone = doc.body.cloneNode(true)
    bodyClone.querySelectorAll('script').forEach((s) => s.remove())
    if (containerRef.current) {
      containerRef.current.innerHTML = bodyClone.innerHTML
    }

    // --- 6. Collect scripts in document order -------------
    // Body-only: index.html already loads any third-party head-level
    // CDN scripts (Tailwind in particular). Re-emitting head scripts
    // here would download + JIT-compile Tailwind a second time on every
    // mount, which the user observed as the page being "very slow".
    // The page's own runtime scripts (pentabay-pages.js etc.) all live
    // at the end of <body>, so body-scope captures everything we need
    // and nothing we shouldn't repeat.
    const scriptNodes = Array.from(doc.body.querySelectorAll('script'))

    let cancelled = false
    const runtimeScripts = []

    /* Download in parallel, still execute in order.
       appendScript() below awaits each external script's `load` before
       appending the next, which is the sync-script semantics the content
       scripts depend on - but it also serialises the 18 round trips, so the
       last sub-route registers 18 sequential fetches after the first. That is
       the window in which the nav is clickable but half the routes do not
       exist yet (see the deferred-routing note in Pentabay.html). Preloading
       warms the HTTP cache for all of them at once, so the ordered chain then
       resolves from cache instead of from the network.
       Measured with 200ms of added latency per request: first->last request
       start spread 2509ms -> 246ms, and the last sub-route registers at 2882ms
       -> 623ms. Execution order is untouched - these links fetch, they do not
       run, so appendScript() below still appends one at a time in document
       order and every content script still sees the globals its predecessor
       defined. */
    doc.body.querySelectorAll('script[src]').forEach((node) => {
      const src = node.getAttribute('src')
      // Same-origin only. A cross-origin preload without a matching
      // crossorigin attribute is fetched twice, which would be slower.
      if (!src || !src.startsWith('/')) return
      const link = document.createElement('link')
      link.rel = 'preload'
      link.as = 'script'
      link.setAttribute(marker, '')
      link.href = src
      document.head.appendChild(link)
      headInjections.push(link)
    })

    function appendScript(node) {
      return new Promise((resolve) => {
        const s = document.createElement('script')
        // Copy attributes (type, src, id, defer, async, etc.)
        for (const attr of Array.from(node.attributes)) {
          s.setAttribute(attr.name, attr.value)
        }
        s.setAttribute(marker, '')
        const srcAttr = node.getAttribute('src')
        if (srcAttr) {
          s.onload = () => resolve()
          s.onerror = () => resolve()
          // Use attribute (already rewritten to root-absolute) rather
          // than node.src, which would resolve against DOMParser's
          // about:blank base URL.
          s.setAttribute('src', srcAttr)
          document.body.appendChild(s)
          runtimeScripts.push(s)
        } else {
          // Inline script - execute synchronously
          s.textContent = node.textContent || ''
          document.body.appendChild(s)
          runtimeScripts.push(s)
          resolve()
        }
      })
    }

    ;(async () => {
      for (const node of scriptNodes) {
        if (cancelled) return
        await appendScript(node)
      }
      /* The inline router has to tell "this route's content script has not
         arrived yet" apart from "no such route" - the two need opposite
         answers, and before this flag existed it could only guess and always
         guessed the second one. Scripts above are awaited one at a time
         (sync-script semantics, see the note on scriptNodes), so on a cold
         load the window between the nav becoming clickable and the last
         sub-route registering is seconds wide. */
      window.PB_SCRIPTS_READY = true
      try { window.dispatchEvent(new CustomEvent('pb-scripts-ready')) } catch (_) {}
    })()

    // --- Cleanup --------------------------------------------------------
    return () => {
      cancelled = true
      window.PB_SCRIPTS_READY = false
      document.title = prevTitle
      for (const { name, prev } of htmlAttrs) {
        if (prev === null) document.documentElement.removeAttribute(name)
        else document.documentElement.setAttribute(name, prev)
      }
      for (const { name, prev } of bodyAttrs) {
        if (prev === null) document.body.removeAttribute(name)
        else document.body.setAttribute(name, prev)
      }
      headInjections.forEach((n) => n.parentNode && n.parentNode.removeChild(n))
      runtimeScripts.forEach((n) => n.parentNode && n.parentNode.removeChild(n))
      // Also strip any nodes the original scripts appended into <head>/<body>
      document.querySelectorAll(`[${marker}]`).forEach((n) => n.remove())
    }
  }, [html, pageKey])

  return <div ref={containerRef} data-pb-page={pageKey} />
}

/**
 * The source HTML files use document-root–relative paths like
 * `assets/maple-leaf.png`. When loaded under a React route other than
 * `/` (e.g. `/canada`), the browser resolves those against the current
 * URL, which lands them on Vite's SPA fallback (HTML) instead of the
 * real asset.
 *
 * We can't use `<base href="/">` because it would also change how
 * fragment-only hash links (`#/services`) are resolved and break the
 * source's hash-router. Instead, walk the parsed doc and prepend `/`
 * to any plain relative URL in well-known asset attributes. Hash-only,
 * already-absolute, protocol-relative, data:/mailto:/tel: URLs are
 * left untouched.
 */
const URL_ATTRS = {
  img:    ['src', 'srcset'],
  source: ['src', 'srcset'],
  script: ['src'],
  link:   ['href'],
  iframe: ['src'],
  video:  ['src', 'poster'],
  audio:  ['src'],
  a:      ['href'],
  use:    ['href', 'xlink:href'],
  image:  ['href', 'xlink:href'],
}
function rewriteRelativeUrls(doc) {
  for (const [tag, attrs] of Object.entries(URL_ATTRS)) {
    for (const el of doc.querySelectorAll(tag)) {
      for (const attr of attrs) {
        const v = el.getAttribute(attr)
        if (!v) continue
        if (/^(?:[a-z][a-z0-9+.-]*:|\/\/|\/|#|mailto:|tel:|javascript:|data:|blob:)/i.test(v)) continue
        if (attr === 'srcset') {
          // srcset is "url 1x, url 2x" - rewrite each URL.
          el.setAttribute(
            attr,
            v.split(',').map((part) => {
              const t = part.trim()
              if (!t) return t
              const sp = t.split(/\s+/)
              if (/^(?:[a-z][a-z0-9+.-]*:|\/\/|\/|#|data:|blob:)/i.test(sp[0])) return t
              sp[0] = '/' + sp[0]
              return sp.join(' ')
            }).join(', ')
          )
        } else {
          el.setAttribute(attr, '/' + v)
        }
      }
    }
  }
}
